import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
	BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
	Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
	BarChart3, Search, X, ChevronDown, TrendingUp, TrendingDown,
	Target, Activity, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { getUserPermissions, isAuthorized } from '../utils/Permissions';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';
import { Checkbox } from './ui/Checkbox';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/Table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/Chart';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/Tabs';
import CalendarHeatmap from './metrics/CalendarHeatmap';
import WeeklyPnlTiles from './metrics/WeeklyPnlTiles';
import MonthlyPnlTiles from './metrics/MonthlyPnlTiles';
import { PropFirmSimulator } from './PropFirmSimulator';
import '../styles/theme.css';

const QUICK_START_COMBOS = [
	{ algo: 'Poseidon', ticker: 'MES' },
	{ algo: 'Poseidon', ticker: 'MNQ' },
	{ algo: 'Fortuna', ticker: 'NQ' },
	{ algo: '1m Ares', ticker: 'MGC' },
	{ algo: '1m Ares Reversal', ticker: 'MGC' },
];

const formatDateForInput = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const formatDollars = (value) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const formatPct = (value) =>
	`${((value || 0) * 100).toFixed(1)}%`;

// Build cumulative P&L from daily data
const buildCumulativePnl = (dailyData) => {
	if (!dailyData?.length) return [];
	let cumulative = 0;
	return dailyData.map((d) => {
		cumulative += d.profit || 0;
		return { date: d.date, cumulative: Math.round(cumulative * 100) / 100 };
	});
};

/* ─── KPI Card ───────────────────────────────────────────── */
function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
	return (
		<Card className="gap-4">
			<CardHeader className="pb-0">
				<CardTitle className="flex items-center text-sm text-muted-foreground">
					{Icon && <Icon className="mr-2 size-4" />}
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className={cn(
					'text-2xl font-bold tabular-nums',
					variant === 'profit' && 'text-green-600',
					variant === 'loss' && 'text-red-600',
				)}>
					{value}
				</div>
				{subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
			</CardContent>
		</Card>
	);
}

/* ─── Sortable Table Header ──────────────────────────────── */
function SortableHead({ label, sortKey, sort, onSort, className = '' }) {
	const isActive = sort.key === sortKey;
	return (
		<TableHead
			className={cn('cursor-pointer select-none hover:text-foreground', className)}
			onClick={() => onSort(sortKey)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				{isActive ? (
					sort.dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />
				) : (
					<ArrowUpDown className="size-3 opacity-30" />
				)}
			</span>
		</TableHead>
	);
}

/* ─── Main Component ─────────────────────────────────────── */
export const AlgoMetrics = () => {
	const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();

	const [permissionsLoaded, setPermissionsLoaded] = useState(false);

	// Data
	const [metrics, setMetrics] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// Table sorting — each table tracks its own sort column and direction
	const [sortByAlgo, setSortByAlgo] = useState({ key: 'netPnl', dir: 'desc' });
	const [sortByTicker, setSortByTicker] = useState({ key: 'netPnl', dir: 'desc' });
	const [sortByCombo, setSortByCombo] = useState({ key: 'netPnl', dir: 'desc' });

	const toggleSort = (setter, key) => {
		setter((prev) => ({
			key,
			dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
		}));
	};

	const sortRows = (rows, { key, dir }) => {
		return [...rows].sort((a, b) => {
			const av = a[key] ?? '';
			const bv = b[key] ?? '';
			if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
			return dir === 'asc' ? av - bv : bv - av;
		});
	};

	// Filters
	const today = new Date();
	const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
	const [startDate, setStartDate] = useState(formatDateForInput(thirtyDaysAgo));
	const [endDate, setEndDate] = useState(formatDateForInput(today));
	const [riskSize, setRiskSize] = useState('250');
	const [comboRiskSizes, setComboRiskSizes] = useState({}); // { "algo|ticker": "300", ... }
	const [filterCombos, setFilterCombos] = useState([]); // array of "algo|ticker" strings
	const [singleDirection, setSingleDirection] = useState(false);
	const [maxTradesPerDay, setMaxTradesPerDay] = useState('');
	const [dailyProfitLimit, setDailyProfitLimit] = useState('');
	const [dailyLossLimit, setDailyLossLimit] = useState('');
	const [drawdownMode, setDrawdownMode] = useState('intra-trade');
	const [dataSource, setDataSource] = useState('live');
	const [allComboOptions, setAllComboOptions] = useState([]); // persisted across queries
	// Load permissions
	useEffect(() => {
		const loadPermissions = async () => {
			if (isAuthenticated && user?.sub) {
				const token = await getAccessTokenSilently();
				const userId = user.sub.split('|')[1];
				await getUserPermissions(token, userId);
				setPermissionsLoaded(true);
			}
		};
		loadPermissions();
	}, [isAuthenticated, user, getAccessTokenSilently]);

	// Pre-fetch available algo+ticker combos
	useEffect(() => {
		if (!permissionsLoaded || !isAuthorized('metricsAdmin')) return;
		const fetchCombos = async () => {
			try {
				const config = await getAuthHeaders();
				const res = await axios.get(
					`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/metrics/combos`,
					{ headers: config.headers, params: config.params }
				);
				const combos = res.data?.combos || [];
				if (combos.length > 0) setAllComboOptions(combos);
			} catch (_) { /* combos will load with first metrics query */ }
		};
		fetchCombos();
	}, [permissionsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auth guard
	if (!permissionsLoaded) {
		return (
			<div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!isAuthorized('metricsAdmin')) {
		return <Navigate to="/connect" replace />;
	}

	const getAuthHeaders = async () => {
		const token = await getAccessTokenSilently();
		const userId = user.sub.split('|')[1];
		return {
			headers: { Authorization: `Bearer ${token}` },
			params: { uid: userId },
		};
	};

	const fetchMetrics = async () => {
		setLoading(true);
		setError(null);
		try {
			const config = await getAuthHeaders();
			// Build per-combo risk sizes: "algo|ticker:riskSize,..." for combos with custom risk
			const hasComboRisks = Object.keys(comboRiskSizes).some(k => comboRiskSizes[k]);
			const comboRiskParam = hasComboRisks
				? filterCombos.map(k => `${k}:${comboRiskSizes[k] || riskSize}`).join(',')
				: undefined;

			const queryParams = {
				...config.params,
				startDate,
				endDate,
				...(filterCombos.length > 0 && { combos: filterCombos.join(',') }),
				...(riskSize && { riskSize }),
				...(comboRiskParam && { comboRiskSizes: comboRiskParam }),
				...(singleDirection && { singleDirection: 'true' }),
				...(maxTradesPerDay && { maxTradesPerDay }),
				...(dailyProfitLimit && { dailyProfitLimit }),
				...(dailyLossLimit && { dailyLossLimit }),
				drawdownMode,
				...(dataSource !== 'live' && { dataSource }),
			};
			const response = await axios.get(
				`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/metrics`,
				{ headers: config.headers, params: queryParams }
			);
			const data = response.data?.data || response.data;
			setMetrics(data);
			// Persist combo options — only update if we got more (unfiltered query)
			if (data?.comboOptions?.length > allComboOptions.length) {
				setAllComboOptions(data.comboOptions);
			}
		} catch (err) {
			setError(err.response?.data?.error || err.message);
		} finally {
			setLoading(false);
		}
	};

	const toggleCombo = (comboKey) => {
		setFilterCombos((prev) =>
			prev.includes(comboKey) ? prev.filter((v) => v !== comboKey) : [...prev, comboKey]
		);
	};

	const clearFilters = () => {
		setFilterCombos([]);
		setRiskSize('250');
		setMaxTradesPerDay('');
		setDailyProfitLimit('');
		setDailyLossLimit('');
		setDrawdownMode('intra-trade');
		setStartDate(formatDateForInput(thirtyDaysAgo));
		setEndDate(formatDateForInput(today));
	};

	const hasActiveFilters = filterCombos.length > 0 || riskSize !== '250' || dailyProfitLimit || dailyLossLimit;

	// Derived data
	const summary = metrics?.summary || {};
	const dailyPnl = metrics?.dailyPnl || [];
	const comboOptions = allComboOptions.length > 0 ? allComboOptions : (metrics?.comboOptions || []);
	const pnlByAlgo = metrics?.pnlByAlgo || [];
	const pnlByTicker = metrics?.pnlByTicker || [];
	const pnlByAlgoTicker = metrics?.pnlByAlgoTicker || [];

	// Quick Start: check if all Quick Start combos are selected
	const quickStartComboKeys = QUICK_START_COMBOS.map((c) => `${c.algo}|${c.ticker}`);
	const isQuickStartActive = quickStartComboKeys.length > 0 &&
		quickStartComboKeys.every((k) => filterCombos.includes(k)) &&
		filterCombos.length === quickStartComboKeys.length;

	const sortedDailyPnl = [...dailyPnl].sort((a, b) => a.date.localeCompare(b.date));
	const cumulativePnl = buildCumulativePnl(sortedDailyPnl);

	const hasMetrics = summary.totalTrades > 0;
	const netPnlVariant = (summary.netPnl || 0) >= 0 ? 'profit' : 'loss';

	return (
		<div className="alm-metrics space-y-4 p-4 md:p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
			<Tabs defaultValue="metrics">
				<div className="flex items-center gap-4 mb-2">
					<h1 className="mr-2 text-xl font-bold text-foreground">Algo Metrics</h1>
					<TabsList>
						<TabsTrigger value="metrics">Metrics</TabsTrigger>
						<TabsTrigger value="simulator">Prop Firm Simulator</TabsTrigger>
					</TabsList>
				</div>

			<TabsContent value="simulator">
				<PropFirmSimulator />
			</TabsContent>

			<TabsContent value="metrics">
			{/* ── Toolbar ───────────────────────────────────── */}
			<div className="space-y-2 mb-4">
				{/* Row 1: Algo selection, dates, risk */}
				<div className="flex flex-wrap items-center gap-2">
					{comboOptions.length > 0 && (
						<Popover>
							<PopoverTrigger asChild>
								<Button variant="outline" size="sm" className="h-8 w-[180px] justify-between text-xs">
									<span className="truncate">
										{filterCombos.length === 0
											? 'All algos'
											: isQuickStartActive
												? 'Quick Start'
												: `${filterCombos.length} selected`}
									</span>
									<ChevronDown className="ml-1.5 size-3 shrink-0" />
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-60 p-2" align="start">
								<div className="max-h-72 space-y-0.5 overflow-y-auto">
									{(() => {
										const available = quickStartComboKeys.filter((k) =>
											comboOptions.some((c) => `${c.algo}|${c.ticker}` === k)
										);
										if (available.length === 0) return null;
										return (
											<label className="flex cursor-pointer items-center gap-2 rounded border-b px-2 py-1.5 text-xs font-medium hover:bg-accent mb-0.5 pb-1.5">
												<Checkbox
													checked={isQuickStartActive}
													onCheckedChange={() => {
														if (isQuickStartActive) {
															setFilterCombos([]);
														} else {
															setFilterCombos([...available]);
														}
													}}
												/>
												Quick Start
											</label>
										);
									})()}
									{comboOptions.map((c) => {
										const key = `${c.algo}|${c.ticker}`;
										return (
											<label
												key={key}
												className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
											>
												<Checkbox
													checked={filterCombos.includes(key)}
													onCheckedChange={() => toggleCombo(key)}
												/>
												{c.algo} — {c.ticker}
											</label>
										);
									})}
								</div>
							</PopoverContent>
						</Popover>
					)}

					<Input type="date" className="h-8 w-[140px] text-xs" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
					<Input type="date" className="h-8 w-[140px] text-xs" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
					<Input type="number" className="h-8 w-[80px] text-xs" placeholder="Risk $" value={riskSize} onChange={(e) => setRiskSize(e.target.value)} min="0" />

					{filterCombos.length > 0 && (
						<Popover>
							<PopoverTrigger asChild>
								<Button variant="outline" size="sm" className="h-8 text-xs">
									Per-Algo Risk
									{Object.keys(comboRiskSizes).some(k => comboRiskSizes[k]) && (
										<span className="ml-1 text-muted-foreground">*</span>
									)}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-72 p-3" align="start">
								<p className="text-xs text-muted-foreground mb-2">
									Override risk size per algo. Blank uses default (${riskSize || '250'}).
								</p>
								<div className="space-y-2">
									{filterCombos.map((key) => (
										<div key={key} className="flex items-center gap-2">
											<span className="text-xs flex-1 truncate">{key.replace('|', ' — ')}</span>
											<Input
												type="number"
												className="h-7 w-[80px] text-xs"
												placeholder={riskSize || '250'}
												value={comboRiskSizes[key] || ''}
												onChange={(e) => setComboRiskSizes(prev => ({ ...prev, [key]: e.target.value }))}
												min="0"
											/>
										</div>
									))}
								</div>
							</PopoverContent>
						</Popover>
					)}

					<Button size="sm" className="h-8 text-xs" onClick={fetchMetrics} disabled={loading}>
						{loading ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Search className="mr-1 size-3" />}
						{loading ? 'Loading...' : 'Apply'}
					</Button>

					{hasActiveFilters && (
						<Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}>
							<X className="mr-1 size-3" /> Clear
						</Button>
					)}
				</div>

				{/* Row 2: Daily limits, filters, mode */}
				<div className="flex flex-wrap items-center gap-2 text-xs">
					<label className="flex items-center gap-1.5 cursor-pointer">
						<Checkbox checked={singleDirection} onCheckedChange={(checked) => setSingleDirection(!!checked)} />
						SDT
					</label>

					<Input type="number" className="h-8 w-[110px] text-xs" placeholder="Max trades/day" value={maxTradesPerDay} onChange={(e) => setMaxTradesPerDay(e.target.value)} min="0" />
					<Input type="number" className="h-8 w-[90px] text-xs" placeholder="DPL $" value={dailyProfitLimit} onChange={(e) => setDailyProfitLimit(e.target.value)} min="0" />
					<Input type="number" className="h-8 w-[90px] text-xs" placeholder="DLL $" value={dailyLossLimit} onChange={(e) => setDailyLossLimit(e.target.value)} min="0" />

					<span className="text-muted-foreground">|</span>

					<div className="flex items-center gap-1.5">
						<label className="flex items-center gap-1 cursor-pointer">
							<input type="radio" name="metricsDrawdownMode" value="intra-trade" checked={drawdownMode === 'intra-trade'} onChange={() => setDrawdownMode('intra-trade')} className="size-3" />
							Intra-trade
						</label>
						<label className="flex items-center gap-1 cursor-pointer">
							<input type="radio" name="metricsDrawdownMode" value="settled" checked={drawdownMode === 'settled'} onChange={() => setDrawdownMode('settled')} className="size-3" />
							Settled
						</label>
					</div>

					<span className="text-muted-foreground">|</span>

					<div className="flex items-center gap-1.5">
						<label className="flex items-center gap-1 cursor-pointer">
							<input type="radio" name="metricsDataSource" value="live" checked={dataSource === 'live'} onChange={() => setDataSource('live')} className="size-3" />
							Live
						</label>
						<label className="flex items-center gap-1 cursor-pointer">
							<input type="radio" name="metricsDataSource" value="backtest" checked={dataSource === 'backtest'} onChange={() => setDataSource('backtest')} className="size-3" />
							Backtest
						</label>
					</div>
				</div>
			</div>

			{/* ── Content ───────────────────────────────────── */}
			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					{error}
				</div>
			)}

			{loading ? (
				<div className="flex items-center justify-center py-20">
					<Loader2 className="size-8 animate-spin text-muted-foreground" />
				</div>
			) : hasMetrics ? (
				<div className="space-y-6">
					{/* ── KPI Cards ──────────────────────────── */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
						<KpiCard
							title="Net P&L"
							value={formatDollars(summary.netPnl)}
							subtitle={`${summary.totalTrades || 0} trades`}
							icon={summary.netPnl >= 0 ? TrendingUp : TrendingDown}
							variant={netPnlVariant}
						/>
						<KpiCard
							title="Win Rate"
							value={formatPct(summary.winRate)}
							subtitle={`${summary.wins || 0}W / ${summary.losses || 0}L`}
							icon={Target}
						/>
						<KpiCard
							title="Profit Factor"
							value={(summary.profitFactor || 0).toFixed(2)}
							subtitle="Gross profit / gross loss"
							icon={Activity}
						/>
						<KpiCard
							title="Max Drawdown"
							value={formatDollars(summary.maxDrawdown)}
							subtitle="End of day"
							icon={TrendingDown}
							variant="loss"
						/>
						{summary.maxIntraDayDrawdown != null && (
							<KpiCard
								title="Max Intra-Day DD"
								value={formatDollars(summary.maxIntraDayDrawdown)}
								subtitle="During open trades"
								icon={TrendingDown}
								variant="loss"
							/>
						)}
					</div>

					{/* ── Charts ──────────────────────────── */}
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Cumulative P&L</CardTitle>
							</CardHeader>
							<CardContent>
								<ChartContainer config={{ cumulative: { label: 'Cumulative P&L', color: 'hsl(var(--chart-1))' } }} className="h-[300px]">
									<LineChart data={cumulativePnl}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="date" tick={{ fontSize: 11 }} />
										<YAxis tick={{ fontSize: 11 }} />
										<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<Line type="monotone" dataKey="cumulative" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
									</LineChart>
								</ChartContainer>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Daily P&L</CardTitle>
							</CardHeader>
							<CardContent>
								<ChartContainer config={{ profit: { label: 'P&L', color: 'hsl(var(--chart-2))' } }} className="h-[300px]">
									<BarChart data={sortedDailyPnl}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="date" tick={{ fontSize: 11 }} />
										<YAxis tick={{ fontSize: 11 }} />
										<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<Bar dataKey="profit" radius={[4, 4, 0, 0]} fill="hsl(var(--chart-2))" />
									</BarChart>
								</ChartContainer>
							</CardContent>
						</Card>
					</div>

					{/* ── Calendar & Period Tiles ─────────────── */}
					<CalendarHeatmap data={sortedDailyPnl} />

					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<WeeklyPnlTiles data={sortedDailyPnl} />
						<MonthlyPnlTiles data={sortedDailyPnl} />
					</div>

					{/* ── Breakdown Tables ────────────────────── */}
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						{/* By Algo */}
						{pnlByAlgo.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="text-sm">P&L by Algorithm</CardTitle>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<SortableHead label="Algorithm" sortKey="algo" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} />
												<SortableHead label="Trades" sortKey="trades" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
												<SortableHead label="Win %" sortKey="winRate" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
												<SortableHead label="Net P&L" sortKey="netPnl" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											</TableRow>
										</TableHeader>
										<TableBody>
											{sortRows(pnlByAlgo, sortByAlgo).map((row, idx) => (
												<TableRow key={row.algo || row.key || idx}>
													<TableCell className="font-medium">{row.algo || row.label || row.key}</TableCell>
													<TableCell className="text-right">{row.trades}</TableCell>
													<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
													<TableCell className="text-right">
														<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
															{formatDollars(row.netPnl)}
														</Badge>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}

						{/* By Ticker */}
						{pnlByTicker.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="text-sm">P&L by Ticker</CardTitle>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<SortableHead label="Ticker" sortKey="ticker" sort={sortByTicker} onSort={(k) => toggleSort(setSortByTicker, k)} />
												<SortableHead label="Trades" sortKey="trades" sort={sortByTicker} onSort={(k) => toggleSort(setSortByTicker, k)} className="text-right" />
												<SortableHead label="Win %" sortKey="winRate" sort={sortByTicker} onSort={(k) => toggleSort(setSortByTicker, k)} className="text-right" />
												<SortableHead label="Net P&L" sortKey="netPnl" sort={sortByTicker} onSort={(k) => toggleSort(setSortByTicker, k)} className="text-right" />
											</TableRow>
										</TableHeader>
										<TableBody>
											{sortRows(pnlByTicker, sortByTicker).map((row, idx) => (
												<TableRow key={row.ticker || row.key || idx}>
													<TableCell className="font-medium">{row.ticker || row.label || row.key}</TableCell>
													<TableCell className="text-right">{row.trades}</TableCell>
													<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
													<TableCell className="text-right">
														<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
															{formatDollars(row.netPnl)}
														</Badge>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}
					</div>

					{/* ── Algo + Ticker Breakdown ────────────── */}
					{pnlByAlgoTicker.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">P&L by Algorithm &amp; Ticker</CardTitle>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<SortableHead label="Algorithm" sortKey="algo" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} />
											<SortableHead label="Ticker" sortKey="ticker" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} />
											<SortableHead label="Trades" sortKey="trades" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Wins" sortKey="wins" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Losses" sortKey="losses" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Win %" sortKey="winRate" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Avg Win" sortKey="avgWin" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Avg Loss" sortKey="avgLoss" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
											<SortableHead label="Net P&L" sortKey="netPnl" sort={sortByCombo} onSort={(k) => toggleSort(setSortByCombo, k)} className="text-right" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{sortRows(pnlByAlgoTicker, sortByCombo).map((row, idx) => (
											<TableRow key={`${row.algo}-${row.ticker}-${idx}`}>
												<TableCell className="font-medium">{row.algo}</TableCell>
												<TableCell>{row.ticker}</TableCell>
												<TableCell className="text-right">{row.trades}</TableCell>
												<TableCell className="text-right text-emerald-600">{row.wins}</TableCell>
												<TableCell className="text-right text-red-600">{row.losses}</TableCell>
												<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgWin)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgLoss)}</TableCell>
												<TableCell className="text-right">
													<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
														{formatDollars(row.netPnl)}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}
				</div>
			) : metrics ? (
				/* No data state */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">No Metrics Data</h3>
					<p className="text-sm text-muted-foreground">
						No algo signals found for the selected date range and filters.
					</p>
				</div>
			) : (
				/* Initial state — no query yet */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">Algo Performance Metrics</h3>
					<p className="text-sm text-muted-foreground">
						Select a date range and click Apply to view algo performance data.
					</p>
				</div>
			)}
			</TabsContent>
			</Tabs>
		</div>
	);
};
